import { Component, Input } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_LUXON_DATE_FORMATS, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { SharedModule } from '@sbd-account-portal/app/shared/shared.module';
import { SharedComponentsModule } from '@sbd-doug-frontend/shared/lib/shared-components/shared-components.module';
import { stateLabelValues } from '@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types';
import {
  blockNegativeNumberKeys,
  getQuestionNumericBounds,
  isDateQuestion,
  isEmailQuestion,
} from '../intake-form.helpers';
import { CIPXQuoteRequestIntakeFormStateBondDetailedQuestion } from '../intake-form.types';
import { CIPXSearchableSelectComponent } from '@sbd-doug-frontend/shared/lib/shared-components/searchable-select/searchable-select.component';

/**
 * Renders a single dynamic question bound to a control inside `group`:
 * the flat additional_questions group for scalar sets, or an item group
 * for collection sets.
 */
@Component({
  standalone: true,
  imports: [
    SharedModule,
    SharedComponentsModule,
    CIPXSearchableSelectComponent,
    MatDatepickerModule,
    MatLuxonDateModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
  ],
  selector: 'cipx-quote-request-intake-form-question-control',
  templateUrl: 'question-control.component.html',
})
export class CIPXQuoteRequestIntakeFormQuestionControlComponent {
  @Input() question!: CIPXQuoteRequestIntakeFormStateBondDetailedQuestion;
  @Input() group!: FormGroup;

  states = stateLabelValues;

  get control(): AbstractControl | null {
    return this.group?.get(this.question.question_code) ?? null;
  }

  get addressGroup(): FormGroup {
    return this.control as FormGroup;
  }

  getLabel(): string {
    return this.question.question_text?.length ? this.question.question_text : this.question.question_label;
  }

  get numericBounds(): { min?: number; max?: number } {
    return getQuestionNumericBounds(this.question);
  }

  get numberStep(): number | 'any' {
    return ['integer', 'int'].includes(this.question.question_type) ? 1 : 'any';
  }

  onNumberKeydown(event: KeyboardEvent): void {
    const min = this.numericBounds.min;
    if (min !== undefined && min >= 0) {
      blockNegativeNumberKeys(event);
    }
  }

  getControlType(): string {
    const type = this.question.question_type;
    if (isDateQuestion(this.question)) {
      return 'date';
    }
    if (isEmailQuestion(this.question)) {
      return 'email';
    }
    if (type === 'address' || type === 'bool' || type === 'state') {
      return type;
    }
    if (type === 'user_selected_option') {
      return this.question.user_selected_options?.type === 'radio' ? 'radio' : 'select';
    }
    if (['integer', 'int', 'decimal', 'float', 'double', 'number', 'currency'].includes(type)) {
      return 'number';
    }
    if (this.question.display?.control === 'textarea') {
      return 'textarea';
    }
    return 'text';
  }

  getSelectOptions(): Array<{ label: string; value: string }> {
    return (this.question.user_selected_options?.options ?? []).map((option) => ({
      label: option,
      value: option,
    }));
  }

  getQuestionErrorText(): string {
    if (this.control?.hasError('email')) {
      return this.question.question_error_text || 'Enter a valid email address.';
    }
    if (this.control?.hasError('min')) {
      return this.question.question_error_text || `Must be ${this.numericBounds.min} or greater.`;
    }
    if (this.control?.hasError('max')) {
      return this.question.question_error_text || `Must be ${this.numericBounds.max} or less.`;
    }
    if (this.question.question_error_text && this.question.question_error_text.length > 0) {
      return this.question.question_error_text;
    }
    if (this.question.question_help_text && this.question.question_help_text.length > 0) {
      return this.question.question_help_text;
    }
    return 'Required.';
  }
}
